import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ManagerSidebar from "../components/Sidebar/ManagerSidebar";
import "./GuardReport.css";
import { useNavigate } from 'react-router-dom';

const API_BASE_URL =
  "https://ikaze-thouse-backend.vercel.app/api/v1";

const GuardReport = () => {
  const navigate = useNavigate();
  const [guards, setGuards] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [selectedGuard, setSelectedGuard] = useState(null);
  const [previewType, setPreviewType] =
    useState("GENERAL");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [periodFilter, setPeriodFilter] =
    useState("ALL");

  // NEW: Date Range States
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const reportRef = useRef();

  useEffect(() => {
    loadData();
    setCurrentPage(1);
  }, [selectedGuard, periodFilter, startDate, endDate]);

  const loadData = async () => {
    try {
      const [gRes, aRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/guards/`),
        axios.get(`${API_BASE_URL}/attendance/`),
      ]);

      setGuards(gRes.data || []);
      setAttendance(aRes.data || []);
    } catch (error) {
      console.error(
        "Error loading data:",
        error
      );
    }
  };

  // ================= HELPERS =================

  const getGuardName = (guardId) => {
    const guard = guards.find(
      (g) => Number(g.id) === Number(guardId)
    );

    return guard ? guard.name : "Unknown";
  };

  const formatTime = (dateString) => {
    if (!dateString) return "--";
    const date = new Date(dateString);

    // Use UTC methods to prevent the 2-hour jump
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
  };

  const formatLate = (minutes) => {
    const mins = Number(minutes || 0);

    if (mins >= 60) {
      const h = Math.floor(mins / 60);
      const m = mins % 60;

      return `${h}h ${m}m`;
    }

    return `${mins} min`;
  };

  // ================= FILTER REPORTS =================

  const getFilteredAttendance = () => {
    const now = new Date();
    let filtered = [...attendance];

    // FILTER BY GUARD
    if (selectedGuard) {
      filtered = filtered.filter(
        (a) =>
          Number(a.guard_id) ===
          Number(selectedGuard.id)
      );
    }

    // NEW: FILTER BY DATE RANGE
    if (startDate) {
      filtered = filtered.filter((a) => {
        const recordDate = new Date(a.date.split("T")[0]);
        const start = new Date(startDate);
        return recordDate >= start;
      });
    }

    if (endDate) {
      filtered = filtered.filter((a) => {
        const recordDate = new Date(a.date.split("T")[0]);
        const end = new Date(endDate);
        return recordDate <= end;
      });
    }

    // WEEKLY
    if (periodFilter === "WEEKLY") {
      filtered = filtered.filter((a) => {
        const recordDate = new Date(a.date);

        const diffTime =
          now.getTime() -
          recordDate.getTime();

        const diffDays =
          diffTime /
          (1000 * 60 * 60 * 24);

        return diffDays <= 7;
      });
    }

    // MONTHLY
    if (periodFilter === "MONTHLY") {
      filtered = filtered.filter((a) => {
        const recordDate = new Date(a.date);

        return (
          recordDate.getMonth() ===
          now.getMonth() &&
          recordDate.getFullYear() ===
          now.getFullYear()
        );
      });
    }

    return filtered;
  };

  const filteredAttendance = getFilteredAttendance();

  // ONLY DISPLAY FIRST 100 RECORDS ON SCREEN
  const screenAttendance = filteredAttendance.slice(0, 100);

  const totalPages = Math.ceil(
    screenAttendance.length / itemsPerPage
  );

  const paginatedAttendance = screenAttendance.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ================= GENERAL STATS =================

  const getGeneralStats = () => {
    const night = filteredAttendance.filter(
      (a) => a.shift_type === "NIGHT"
    );

    const daily = {};

    night.forEach((r) => {
      const date = r.date?.split("T")[0];

      if (!date) return;

      daily[date] =
        (daily[date] || 0) + 1;
    });

    const values = Object.values(daily);

    const totalRating =
      filteredAttendance.reduce(
        (s, a) =>
          s + (Number(a.rating) || 0),
        0
      );

    const avgRating =
      filteredAttendance.length
        ? (
          totalRating /
          filteredAttendance.length
        ).toFixed(1)
        : 0;

    const performance = (
      (Number(avgRating) / 5) *
      100
    ).toFixed(1);

    return {
      total: filteredAttendance.length,

      days2: values.filter(
        (v) => v >= 2
      ).length,

      days1: values.filter(
        (v) => v === 1
      ).length,

      late: filteredAttendance.reduce(
        (s, a) =>
          s +
          (Number(a.late_minutes) || 0),
        0
      ),

      avgRating,

      performance,
    };
  };

  // ================= INDIVIDUAL STATS =================

  const getSafeIndividualStats = (
    guardId
  ) => {
    if (!guardId) return null;

    const id = Number(guardId);

    const data =
      filteredAttendance.filter(
        (a) =>
          Number(a.guard_id) === id
      );

    const totalRating = data.reduce(
      (s, a) =>
        s + (Number(a.rating) || 0),
      0
    );

    const avgRating = data.length
      ? totalRating / data.length
      : 0;

    return {
      data,

      totalDays: data.length,

      totalLate: data.reduce(
        (s, a) =>
          s +
          (Number(a.late_minutes) || 0),
        0
      ),

      onTime: data.filter(
        (a) =>
          Number(a.late_minutes) === 0
      ).length,

      avgRating: avgRating.toFixed(1),

      performance: (
        (avgRating / 5) *
        100
      ).toFixed(1),
    };
  };

  const general = getGeneralStats();

  const individual = selectedGuard
    ? getSafeIndividualStats(
      selectedGuard.id
    )
    : null;

  // ================= DELETE =================

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this attendance record?"
    );

    if (!confirmDelete) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/attendance/${id}`
      );

      setAttendance((prev) =>
        prev.filter((a) => a.id !== id)
      );

      alert(
        "Attendance deleted successfully"
      );
    } catch (error) {
      console.error(error);

      alert(
        "Failed to delete attendance"
      );
    }
  };

  // ================= PDF EXPORT =================

  const downloadPDF = () => {
    const pdf = new jsPDF(
      "p",
      "mm",
      "a4"
    );

    const pageWidth =
      pdf.internal.pageSize.getWidth();

    const pageHeight =
      pdf.internal.pageSize.getHeight();

    const margin = 12;

    // HEADER
    const headerHeight = 25;

    pdf.setFillColor(37, 99, 235);

    pdf.rect(
      0,
      0,
      pageWidth,
      headerHeight,
      "F"
    );

    // LOGO
    const logoSize = 12;
    const logoX = margin;
    const logoY = 6.5;

    pdf.setFillColor(255, 255, 255);

    pdf.roundedRect(
      logoX,
      logoY,
      logoSize,
      logoSize,
      2,
      2,
      "F"
    );

    pdf.setFont(
      "helvetica",
      "bold"
    );

    pdf.setFontSize(10);

    pdf.setTextColor(37, 99, 235);

    pdf.text(
      "TH",
      logoX + 3,
      logoY + 8
    );

    // TITLE
    pdf.setTextColor(255, 255, 255);

    pdf.setFontSize(15);

    pdf.text(
      "GKM Invest",
      logoX + logoSize + 6,
      logoY + 6
    );

    pdf.setFontSize(8);

    pdf.setFont(
      "helvetica",
      "normal"
    );

    pdf.setTextColor(219, 234, 254);

    const reportType = selectedGuard
      ? `Guard: ${selectedGuard.name}`
      : "General Overview";

    // Show date range in PDF if selected
    const rangeInfo = (startDate || endDate)
      ? ` | Range: ${startDate || 'Start'} to ${endDate || 'End'}`
      : ` | ${periodFilter} REPORT`;

    pdf.text(
      `${reportType}${rangeInfo} | Issued: ${new Date().toLocaleDateString()}`,
      logoX + logoSize + 6,
      logoY + 11
    );

    // STATS
    const stats = selectedGuard
      ? individual
      : general;

    const cards = selectedGuard
      ? [
        {
          l: "Total Days",
          v: stats.totalDays,
          c: [37, 99, 235],
        },

        {
          l: "Late Minutes",
          v: formatLate(
            stats.totalLate
          ),
          c: [245, 158, 11],
        },

        {
          l: "Avg Rating",
          v: `${stats.avgRating}/5`,
          c: [6, 182, 212],
        },

        {
          l: "Efficiency",
          v: `${stats.performance}%`,
          c: [239, 68, 68],
        },

        {
          l: "On-Time",
          v: stats.onTime,
          c: [16, 185, 129],
        },

        {
          l: "Status",
          v:
            Number(
              stats.performance
            ) >= 80
              ? "Elite"
              : "Standard",
          c: [139, 92, 246],
        },
      ]
      : [
        {
          l: "Total Attendance",
          v: stats.total,
          c: [37, 99, 235],
        },

        {
          l: "2-Guard Nights",
          v: `${stats.days2} days`,
          c: [139, 92, 246],
        },

        {
          l: "1-Guard Nights",
          v: `${stats.days1} days`,
          c: [16, 185, 129],
        },

        {
          l: "Total Late",
          v: formatLate(
            stats.late
          ),
          c: [245, 158, 11],
        },

        {
          l: "Avg Rating",
          v: `${stats.avgRating}/5`,
          c: [6, 182, 212],
        },

        {
          l: "Performance",
          v: `${stats.performance}%`,
          c: [239, 68, 68],
        },
      ];

    const cols = 3;
    const rows = 2;
    const cardGap = 4;

    const cardW =
      (pageWidth -
        margin * 2 -
        cardGap * (cols - 1)) /
      cols;

    const cardH = 16;

    let startY = 32;

    cards.forEach((item, i) => {
      const col = i % cols;

      const row = Math.floor(i / cols);

      const x =
        margin +
        col * (cardW + cardGap);

      const y =
        startY +
        row * (cardH + cardGap);

      pdf.setFillColor(
        255,
        255,
        255
      );

      pdf.setDrawColor(
        230,
        230,
        230
      );

      pdf.roundedRect(
        x,
        y,
        cardW,
        cardH,
        1.5,
        1.5,
        "FD"
      );

      pdf.setFillColor(
        item.c[0],
        item.c[1],
        item.c[2]
      );

      pdf.rect(
        x,
        y,
        1.5,
        cardH,
        "F"
      );

      pdf.setFontSize(7);

      pdf.setTextColor(
        100,
        116,
        139
      );

      pdf.setFont(
        "helvetica",
        "bold"
      );

      pdf.text(
        item.l.toUpperCase(),
        x + 4,
        y + 6
      );

      pdf.setFontSize(10);

      pdf.setTextColor(
        30,
        41,
        59
      );

      pdf.text(
        String(item.v),
        x + 4,
        y + 12
      );
    });

    // TABLE
    const tableData =
      filteredAttendance.map((a) => [
        selectedGuard
          ? selectedGuard.name
          : getGuardName(a.guard_id),

        a.date?.split("T")[0] || "--",

        formatTime(a.check_in),

        formatTime(a.check_out),

        formatLate(a.late_minutes),

        a.rating || 0,

        `${(
          (Number(a.rating || 0) /
            5) *
          100
        ).toFixed(0)}%`,
      ]);

    autoTable(pdf, {
      startY:
        startY +
        rows *
        (cardH + cardGap) +
        8,

      head: [
        [
          "Guard Name",
          "Date",
          "In",
          "Out",
          "Late Time",
          "Rating",
          "Score",
        ],
      ],

      body: tableData,

      theme: "grid",

      headStyles: {
        fillColor: [30, 41, 59],
        fontSize: 9,
        fontStyle: "bold",
        halign: "center",
      },

      styles: {
        fontSize: 8,
        cellPadding: 3,
      },

      columnStyles: {
        0: { cellWidth: 40 },
        1: {
          halign: "center",
        },
        2: {
          halign: "center",
        },
        3: {
          halign: "center",
        },
        4: {
          halign: "center",
        },
        5: {
          halign: "center",
        },
        6: {
          halign: "center",
        },
      },

      margin: {
        left: margin,
        right: margin,
      },
    });

    // FOOTER
    pdf.setFontSize(7);

    pdf.setTextColor(160);

    pdf.text(
      "GKM Invest - Professional Monitoring System | Confidential",
      pageWidth / 2,
      pageHeight - 8,
      {
        align: "center",
      }
    );

    pdf.save(
      `GKM_Report_${selectedGuard
        ? selectedGuard.name.replace(
          /\s+/g,
          "_"
        )
        : "General"
      }.pdf`
    );
  };

  return (
    <div className="guard-wrapper">
      <ManagerSidebar />

      <div
        className="guard-content"
        ref={reportRef}
      >
        <h2 className="title">
          📊 Guard Performance Report
        </h2>

        {/* CONTROLS */}
        <div className="header-actions">
          <button
            className={`btn ${previewType === "GENERAL"
              ? "active-btn"
              : ""
              }`}
            onClick={() => {
              setPreviewType(
                "GENERAL"
              );

              setSelectedGuard(null);
            }}
          >
            General Overview
          </button>

          {/* GUARD FILTER */}
          <select
            className="select-box"
            value={
              selectedGuard?.id || ""
            }
            onChange={(e) => {
              const guard = guards.find(
                (g) =>
                  String(g.id) ===
                  e.target.value
              );

              setSelectedGuard(
                guard || null
              );

              if (guard) {
                setPreviewType(
                  "INDIVIDUAL"
                );
              } else {
                setPreviewType(
                  "GENERAL"
                );
              }
            }}
          >
            <option value="">
              Search By Guard Name
            </option>

            {guards.map((g) => (
              <option
                key={g.id}
                value={g.id}
              >
                {g.name}
              </option>
            ))}
          </select>

          {/* PERIOD FILTER */}
          <select
            className="select-box"
            value={periodFilter}
            onChange={(e) =>
              setPeriodFilter(
                e.target.value
              )
            }
          >
            <option value="ALL">
              All Reports
            </option>

            <option value="WEEKLY">
              Weekly Report
            </option>

            <option value="MONTHLY">
              Monthly Report
            </option>
          </select>

          {/* NEW: DATE RANGE FILTERS */}
          <div className="date-range-container" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="date"
              className="select-box"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              title="Start Date"
            />
            <span style={{ color: '#64748b', fontSize: '12px', fontWeight: 'bold' }}>To</span>
            <input
              type="date"
              className="select-box"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              title="End Date"
            />
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(""); setEndDate(""); }}
                style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
                title="Clear Dates"
              >
                ×
              </button>
            )}
          </div>

          <button
            className="download-btn"
            onClick={downloadPDF}
          >
            Download PDF
          </button>

          <button
            onClick={() => navigate('/guards')}
            style={{
              background: ' blue',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)',
              padding: '12px 18px',
              borderRadius: '12px',
              cursor: 'pointer',
              marginRight: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backdropFilter: 'blur(10px)',
              transition: '0.3s',
              fontWeight: '600'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <span>👤</span> All Guards
          </button>

          <button
            onClick={() => navigate('/attendance')}
            style={{
              background: 'linear-gradient(135deg, #2563eb, #1b06d4)',
              color: 'white',
              border: 'none',
              padding: '12px 18px',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(37,99,235,0.4)',
              transition: '0.3s'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'
            }
          >
            <span>📝</span> Record Attendance
          </button>
        </div>

        {/* GENERAL DASHBOARD */}
        <div className="metrics-grid">
          <div className="metric-card blue small">
            <h4>Total Shifts</h4>
            <h2>{general.total}</h2>
          </div>

          <div className="metric-card purple small">
            <h4>2-Guard Nights</h4>
            <h2>{general.days2}</h2>
          </div>

          <div className="metric-card green small">
            <h4>1-Guard Nights</h4>
            <h2>{general.days1}</h2>
          </div>

          <div className="metric-card orange small">
            <h4>Total Late</h4>
            <h2>
              {formatLate(
                general.late
              )}
            </h2>
          </div>

          <div className="metric-card cyan small">
            <h4>Avg Rating</h4>
            <h2>
              {general.avgRating}/5
            </h2>
          </div>

          <div className="metric-card red small">
            <h4>Performance</h4>
            <h2>
              {general.performance}%
            </h2>
          </div>
        </div>

        {/* INDIVIDUAL DASHBOARD */}
        {selectedGuard &&
          individual && (
            <>
              <h3 className="subtitle">
                📌{" "}
                {selectedGuard.name}{" "}
                Overview
              </h3>

              <div className="metrics-grid">
                <div className="metric-card green small">
                  <h4>Total Days</h4>

                  <h2>
                    {
                      individual.totalDays
                    }
                  </h2>
                </div>

                <div className="metric-card orange small">
                  <h4>Total Late</h4>

                  <h2>
                    {formatLate(
                      individual.totalLate
                    )}
                  </h2>
                </div>

                <div className="metric-card cyan small">
                  <h4>Avg Rating</h4>

                  <h2>
                    {
                      individual.avgRating
                    }
                    /5
                  </h2>
                </div>

                <div className="metric-card red small">
                  <h4>Performance</h4>

                  <h2>
                    {
                      individual.performance
                    }
                    %
                  </h2>
                </div>
              </div>
            </>
          )}

        {/* TABLE */}
        <div className="table-wrapper">
          <table className="modern-table">
            <thead>
              <tr
              // style={{
              //   background: "linear-gradient(135deg, #1e3c72, #2a5298)",

              // }}
              >
                <th
                  style={{
                    background: "linear-gradient(135deg, #1e3c72, #2a5298)",
                    color: "#fff",
                    padding: "14px 18px",
                    textAlign: "center",
                    fontSize: "15px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                    borderTopLeftRadius: "12px",
                  }}
                >
                  Guard
                </th>

                <th
                  style={{
                    background: "linear-gradient(135deg, #1e3c72, #2a5298)",
                    color: "#fff",
                    padding: "14px 18px",
                    textAlign: "center",
                    fontSize: "15px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                  }}
                >
                  Date
                </th>

                <th
                  style={{
                    background: "linear-gradient(135deg, #1e3c72, #2a5298)",
                    color: "#fff",
                    padding: "14px 18px",
                    textAlign: "center",
                    fontSize: "15px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                  }}
                >
                  In
                </th>

                <th
                  style={{
                    background: "linear-gradient(135deg, #1e3c72, #2a5298)",
                    color: "#fff",
                    padding: "14px 18px",
                    textAlign: "center",
                    fontSize: "15px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                  }}
                >
                  Out
                </th>

                <th
                  style={{
                    background: "linear-gradient(135deg, #1e3c72, #2a5298)",
                    color: "#fff",
                    padding: "14px 18px",
                    textAlign: "center",
                    fontSize: "15px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                  }}
                >
                  Late
                </th>

                <th
                  style={{
                    background: "linear-gradient(135deg, #1e3c72, #2a5298)",
                    color: "#fff",
                    padding: "14px 18px",
                    textAlign: "center",
                    fontSize: "15px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                  }}
                >
                  Rating
                </th>

                <th
                  style={{
                    background: "linear-gradient(135deg, #1e3c72, #2a5298)",
                    color: "#fff",
                    padding: "14px 18px",
                    textAlign: "center",
                    fontSize: "15px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                  }}
                >
                  Perf %
                </th>

                <th
                  style={{
                    background: "linear-gradient(135deg, #1e3c72, #2a5298)",
                    color: "#fff",
                    padding: "14px 18px",
                    textAlign: "center",
                    fontSize: "15px",
                    fontWeight: "600",
                    letterSpacing: "0.5px",
                    borderTopRightRadius: "12px",
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedAttendance.map((a, i) => (
                <tr key={i}>
                  <td>
                    <b>
                      {selectedGuard
                        ? selectedGuard.name
                        : getGuardName(a.guard_id)}
                    </b>
                  </td>

                  <td>
                    {
                      a.date?.split(
                        "T"
                      )[0]
                    }
                  </td>

                  <td>
                    {formatTime(
                      a.check_in
                    )}
                  </td>

                  <td>
                    {formatTime(
                      a.check_out
                    )}
                  </td>

                  <td>
                    {formatLate(
                      a.late_minutes
                    )}
                  </td>

                  <td>
                    {a.rating}
                  </td>

                  <td>
                    {(
                      (Number(
                        a.rating || 0
                      ) /
                        5) *
                      100
                    ).toFixed(1)}
                    %
                  </td>

                  {/* ACTIONS */}
                  <td>
                    <div
                      style={{
                        display:
                          "flex",
                        gap: "8px",
                        justifyContent:
                          "center",
                      }}
                    >
                      <button
                        onClick={() => navigate(`/Attendence/Edit/${a.id}`)}
                        style={{
                          background:
                            "#2563eb",
                          color:
                            "#fff",
                          border:
                            "none",
                          padding:
                            "6px 10px",
                          borderRadius:
                            "6px",
                          cursor:
                            "pointer",
                          fontSize:
                            "12px",
                        }}
                      >
                        Edit
                      </button>

                      <button
                        onClick={() =>
                          handleDelete(
                            a.id
                          )
                        }
                        style={{
                          background:
                            "#dc2626",
                          color:
                            "#fff",
                          border:
                            "none",
                          padding:
                            "6px 10px",
                          borderRadius:
                            "6px",
                          cursor:
                            "pointer",
                          fontSize:
                            "12px",
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              )
              )}
            </tbody>
          </table>
          <div className="pagination-container">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              className="page-btn"
              style={{
                padding: "8px 14px",
                margin: "0 4px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                background: currentPage === 1 ? "#e2e8f0" : "#0433a0",
                color: currentPage === 1 ? "#94a3b8" : "#fff",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                fontWeight: "bold",
                transition: "0.3s"
              }}
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index + 1)}
                style={{
                  padding: "8px 12px",
                  margin: "0 4px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  background: currentPage === index + 1 ? "#5db1f5" : "#fff",
                  color: currentPage === index + 1 ? "#fff" : "#334155",
                  cursor: "pointer",
                  fontWeight: "bold",
                  transition: "0.3s",
                  boxShadow:
                    currentPage === index + 1
                      ? "0 4px 10px rgba(37,99,235,0.4)"
                      : "none"
                }}
              >
                {index + 1}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              style={{
                padding: "8px 14px",
                margin: "0 4px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                background: currentPage === totalPages ? "#e2e8f0" : "#032880",
                color: currentPage === totalPages ? "#94a3b8" : "#fff",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                fontWeight: "bold",
                transition: "0.3s"
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuardReport;